import { uniqueId, find, get } from 'lodash';

import ErrorHandler from 'store/utils/ErrorHandler';
import { nameNewFile } from 'store/utils/fileSystem';
import Notify from 'store/utils/Notify';
import downloadFile from 'store/utils/downloadFile';
import { getIsProjectInnerPageSelector } from 'store/services/router/selectors';
import { containsTemporaryFile } from './utils';

import {
  getAllFiles,
  getFilesMapAsObject,
  getObsoleteOpenedFiles,
} from './selectors';
import * as types from './actionTypes';
import * as api from './api';

const genId = () => uniqueId('df-file-');

export const getFileInfoFromFilepath = filePath => {
  const filePathSplitted = filePath.split('/');

  return filePathSplitted.reduce(
    (acc, partOfPath, index) => {
      if (index === filePathSplitted.length - 1) {
        return { ...acc, name: partOfPath };
      }

      const path = index === 0 ? partOfPath : [acc.path, partOfPath].join('/');
      return { ...acc, path };
    },
    { name: '', path: '' },
  );
};

/**
 * Export all files
 */
export const exportAllFilesAndFolders = () => async dispatch => {
  try {
    dispatch({
      type: types.EXPORT_FLOW_ALL_FILES_AND_FOLDERS_REQUEST,
    });

    const { data } = await api.exportFilesAndFoldersRequest();

    dispatch({
      type: types.EXPORT_FLOW_ALL_FILES_AND_FOLDERS_SUCCESS,
    });

    downloadFile(data, 'flow.zip');
  } catch (error) {
    dispatch({
      type: types.EXPORT_FLOW_ALL_FILES_AND_FOLDERS_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred while exporting files',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Export all files
 */
export const syncFlowFiles = () => async dispatch => {
  try {
    dispatch({
      type: types.SYNC_FLOW_FILES_REQUEST,
    });

    await api.syncFlowFilesRequest();

    dispatch({
      type: types.SYNC_FLOW_FILES_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: types.SYNC_FLOW_FILES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred while exporting files',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const getFlowFilesRequest = () => ({
  type: types.GET_FLOW_FILES_REQUEST,
});

export const getFlowFilesSuccess = files => ({
  type: types.GET_FLOW_FILES_SUCCESS,
  payload: { files },
});

export const getFlowFilesError = error => ({
  type: types.GET_FLOW_FILES_ERROR,
  payload: { error },
});

export const getFilesAndFolders = () => async (dispatch, getState) => {
  const state = getState();
  try {
    dispatch(getFlowFilesRequest());

    await dispatch(syncFlowFiles());

    const { data: responseData } = await api.getFilesAndFoldersRequest();

    // We need to add to data results if the file was already opened in order to
    // not lose that info, so we will be able to fetch the opened files again if we need to.
    // Thats why we use stateFilesMap & normalizedData
    const stateFilesMap = getFilesMapAsObject(state);

    // TODO: This should really be in the related reducer
    const normalizedData = responseData.map(file => ({
      ...stateFilesMap[file.path],
      ...file,
      id: file.path,
    }));

    dispatch(getFlowFilesSuccess(normalizedData));
  } catch (error) {
    dispatch(getFlowFilesError(get(error, 'response.data')));

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const getFlowFileRequest = ({ id, isOpened = false }) => ({
  type: types.GET_FLOW_FILE_REQUEST,
  payload: { id, isOpened },
});

export const getFlowFileSuccess = ({
  id,
  isOpened,
  openedAt = new Date().getTime(),
  data,
}) => ({
  type: types.GET_FLOW_FILE_SUCCESS,
  payload: { id, isOpened, openedAt, data },
});

export const getFlowFileError = error => ({
  type: types.GET_FLOW_FILE_ERROR,
  payload: { error },
});

export const getFileOrFolder = (file, isOpened = false) => async dispatch => {
  try {
    dispatch(getFlowFileRequest({ id: file.id, isOpened }));

    // The file name is removed from the part property and the name property
    // contains only the file name without the whole path. This is what the API
    // call expects.
    const normalizedFile = {
      ...file,
      ...getFileInfoFromFilepath(file.path),
    };

    const { data } = await api.getFileOrFolderRequest(normalizedFile);

    dispatch(getFlowFileSuccess({ id: file.id, isOpened, data }));
  } catch (error) {
    dispatch(getFlowFileError(error.response.data));

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

// eslint-disable-next-line
export const openFile = file => dispatch => {
  if (file && (file.contents || file.contents === '')) {
    dispatch({
      type: types.OPEN_FLOW_FILE,
      payload: {
        id: file.id,
      },
    });
    return;
  }

  dispatch(getFileOrFolder(file));
};

/**
 * Add file
 */
export const addFile = data => async dispatch => {
  try {
    dispatch({
      type: types.ADD_FLOW_FILE_REQUEST,
    });

    await api.addFileRequest(data.path);
    await api.addFileRequest(data.path);

    dispatch({
      type: types.ADD_FLOW_FILE_SUCCESS,
    });

    Notify.success({
      title: 'Blob was created successfully',
    });
  } catch (error) {
    dispatch({
      type: types.ADD_FLOW_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to Add File',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const addFolder = data => async dispatch => {
  try {
    dispatch({
      type: types.ADD_FLOW_FOLDER_REQUEST,
    });

    await api.addFolderRequest(data);

    dispatch({
      type: types.ADD_FLOW_FOLDER_SUCCESS,
    });

    Notify.success({
      title: 'Folder was created successfully',
    });
  } catch (error) {
    dispatch({
      type: types.ADD_FLOW_FOLDER_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to Add Folder',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Upload files
 */
export const uploadFiles = (files, path, shouldOverwriteFiles) => async (
  dispatch,
  getState,
) => {
  try {
    const state = getState();

    dispatch({
      type: types.UPLOAD_FLOW_FILES_REQUEST,
    });

    await api.uploadFilesRequest(files, path, shouldOverwriteFiles);

    dispatch({
      type: types.UPLOAD_FLOW_FILES_SUCCESS,
    });

    Notify.success({
      title: `${
        files.length === 1 ? 'Blob was' : 'Blobs were'
      } uploaded successfully`,
    });

    await dispatch(getFilesAndFolders());
    if (shouldOverwriteFiles) {
      const obsoleteFiles = getObsoleteOpenedFiles(state);

      for (const obsoleteFile of obsoleteFiles) {
        dispatch(getFileOrFolder(obsoleteFile, true));
      }
    }
  } catch (err) {
    const { status } = err.response;

    // Show notification only when error is not about conflicts
    if (status !== 409) {
      Notify.error({
        title: 'Fail to upload file',
        description: ErrorHandler.getNotificationError(err),
      });
    }

    const conflicts = get(err, 'response.data.conflicts', null);

    dispatch({
      type: types.UPLOAD_FLOW_FILES_ERROR,
      payload: {
        error: err.message,
        conflicts,
      },
    });

    // eslint-disable-next-line no-throw-literal
    throw err;
  }
};

/**
 * Update current files
 */
export const updateFiles = files => async dispatch =>
  Promise.all(files.map(file => dispatch(getFileOrFolder(file, true))));

export const resetUploadFiles = () => dispatch => {
  dispatch({
    type: types.RESET_FLOW_UPLOAD_FILES,
  });
};

export const renameFile = data => async dispatch => {
  try {
    await dispatch({
      type: types.RENAME_FLOW_FILE_REQUEST,
    });
    const { newName, id } = data;
    const { name, path } = getFileInfoFromFilepath(id);

    await api.renameFileRequest({
      oldFileName: name,
      newName,
      path,
    });

    dispatch({
      type: types.RENAME_FLOW_FILE_SUCCESS,
      payload: { newName, id },
    });

    Notify.success({
      title: 'File was renamed successfully',
    });
  } catch (error) {
    dispatch({
      type: types.RENAME_FLOW_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to rename file',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

export const renameFolder = data => async dispatch => {
  try {
    dispatch({
      type: types.RENAME_FLOW_FOLDER_REQUEST,
    });
    const { newName, id } = data;

    // It would be better if the path was a parameter
    // instead of trying to find it with magic
    const { path } = getFileInfoFromFilepath(id);

    const { data: responseData } = await api.renameFolderRequest({
      newName,
      path: id,
      parentPath: path,
    });
    const { name: updatedName, path: updatedPath } = responseData;

    dispatch({
      type: types.RENAME_FLOW_FOLDER_SUCCESS,
      payload: { id, updatedName, updatedPath },
    });

    Notify.success({
      title: 'Folder was renamed successfully',
    });
  } catch (error) {
    dispatch({
      type: types.RENAME_FLOW_FOLDER_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to rename folder',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

// /**
//  * Update file
//  */
export const updateFile = (file, data) => async dispatch => {
  try {
    await dispatch({
      type: types.UPDATE_FLOW_FILE_REQUEST,
    });

    await api.updateFileRequest(file.path, data);

    dispatch({
      type: types.UPDATE_FLOW_FILE_SUCCESS,
    });

    Notify.success({
      title: 'Blob was updated successfully',
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_FLOW_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to update Blob',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const updateFolder = folder => async dispatch => {
  try {
    dispatch({
      type: types.UPDATE_FLOW_FOLDER_REQUEST,
    });

    await api.updateFolderRequest({ name: folder.name, path: folder.path });

    dispatch({
      type: types.UPDATE_FLOW_FOLDER_SUCCESS,
      payload: { id: folder.id },
    });

    Notify.success({
      title: 'Folder was updated successfully',
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_FLOW_FOLDER_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to update Blob',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add entry
 */
export const addNewFile = () => (dispatch, getState) => {
  const name = nameNewFile(getAllFiles(getState()), 'New File');

  dispatch({
    type: types.ADD_FLOW_NEW_FILE,
    payload: {
      file: {
        id: genId(),
        name,
        path: '',
        isNew: true,
      },
    },
  });
};

/**
 * Delete entry
 */
export const deleteFile = data => async dispatch => {
  try {
    dispatch({
      type: types.DELETE_FLOW_FILE_REQUEST,
    });
    const { name, path } = getFileInfoFromFilepath(data.id);

    await api.deleteFileRequest({ name, path });

    dispatch({
      type: types.DELETE_FLOW_FILE_SUCCESS,
      payload: { id: data.id },
    });

    Notify.success({
      title: 'Blob was deleted successfully',
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_FLOW_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to delete Blob',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

/**
 * Delete Folder
 */
export const deleteFolder = data => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.DELETE_FLOW_FOLDER_REQUEST,
    });

    await api.deleteFolderRequest(data.id);

    if (getIsProjectInnerPageSelector(getState())) {
      // Instead of dispatching an action with the type DELETE_FOLDER_SUCCESS,
      // we are fetching the data again from the backend
      // instead of creating the tree again. This is a workaround
      // to fix CONVO-1612
      await dispatch(getFilesAndFolders());
    }

    Notify.success({
      title: 'Folder was deleted successfully',
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_FLOW_FOLDER_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to delete Folder',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

/**
 * Export file
 */
export const exportFileOrFolder = fileOrFolder => async dispatch => {
  try {
    dispatch({
      type: types.EXPORT_FLOW_FILE_OR_FOLDER_REQUEST,
    });

    const { data: exportedData } = await api.exportFilesAndFoldersRequest({
      paths: [fileOrFolder.id],
    });

    dispatch({
      type: types.EXPORT_FLOW_FILE_OR_FOLDER_SUCCESS,
    });

    downloadFile(exportedData, `export.zip`);
  } catch (error) {
    dispatch({
      type: types.EXPORT_FLOW_FILE_OR_FOLDER_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update Blob's contents
 */
export const updateBlob = (file, value, snoozeNotification = false) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.UPDATE_FLOW_BLOB_REQUEST,
    });

    const { path } = getFileInfoFromFilepath(file.path);

    const normalizedFile = {
      name: file.name,
      path,
    };

    await api.updateBlobRequest(normalizedFile, value);

    const files = getAllFiles(getState());

    dispatch({
      type: types.UPDATE_FLOW_BLOB_SUCCESS,
      payload: {
        file,
        contents: value,
        files,
      },
    });

    if (!snoozeNotification) {
      Notify.success({
        title: `${file.path} was updated successfully`,
      });
    }
  } catch (error) {
    await dispatch({
      type: types.UPDATE_FLOW_BLOB_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to update Blob',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Close File
 */
export const closeEditorFile = ({ path }) => (dispatch, getState) => {
  const file = find(getAllFiles(getState()), { path });

  if (file && file.id) {
    dispatch({
      type: types.CLOSE_FLOW_FILE,
      payload: {
        id: file.id,
      },
    });
  }
};

/**
 * Update editor searchTerm
 */
export const updateEditorSearchTerm = searchTerm => ({
  type: types.UPDATE_FLOW_EDITOR_SEARCH_TERM,
  payload: {
    searchTerm,
  },
});

/**
 * set theme
 */
export const setTheme = theme => ({
  type: types.SET_FLOW_THEME,
  payload: {
    theme,
  },
});

/**
 * Delete multiple files
 */
export const deleteFiles = (items = []) => async dispatch => {
  try {
    const deletedIds = items.map(item => item.id);

    dispatch({
      type: types.DELETE_FLOW_FILES_REQUEST,
    });

    const isRootItem = item => item.path.includes('/');

    const itemsToDelete = items.reduce(
      (acc, item) => (isRootItem(item) ? acc : [...acc, item.path]),
      [],
    );

    await api.deleteFilesRequest({
      delete: itemsToDelete,
    });

    dispatch({
      type: types.DELETE_FLOW_FILES_SUCCESS,
      payload: {
        deletedIds,
      },
    });

    Notify.success({
      title: `${
        deletedIds.length > 1 ? 'Files were' : 'file was'
      } deleted successfully`,
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_FLOW_FILES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Open files
 */
export const openFiles = (files = []) => async dispatch => {
  dispatch({
    type: types.OPEN_FLOW_FILES_REQUEST,
  });

  try {
    /* eslint-disable */
    for (const file of files) {
      await dispatch(openFile(file));
    }

    dispatch({
      type: types.OPEN_FLOW_FILES_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: types.OPEN_FLOW_FILES_ERROR,
      payload: {
        error: 'Error',
      },
    });
  }
};

export const createNewTempFile = ({ path = '' } = {}) => ({
  type: types.CREATE_TEMP_FILE,
  payload: { path },
});

export const createNewTempFolder = ({ path = '' } = {}) => ({
  type: types.CREATE_TEMP_FOLDER,
  payload: { path },
});

const persistTemporaryItemSuccess = item => ({
  type: types.PERSIST_TEMPORARY_ITEM_SUCCESS,
  payload: { item },
});

const persistTemporaryItemFailure = item => ({
  type: types.PERSIST_TEMPORARY_ITEM_FAILURE,
  payload: { item },
});

export const persistTemporaryItem = item => async (dispatch, getState) => {
  if (!containsTemporaryFile(getAllFiles(getState()))) {
    return;
  }

  try {
    await (item.isFolder
      ? api.addFolderRequest(item)
      : api.addFileRequest(item));

    dispatch(persistTemporaryItemSuccess(item));

    Notify.success({
      title: `${item.isFolder ? 'Folder' : 'File'} was created successfully`,
    });

    if (getIsProjectInnerPageSelector(getState())) {
      // This action is dispatched for the sole reason to force a re-render for
      // the Tree component. This is a workaround due to limitation imposed by
      // the design of the react component.
      await dispatch(getFilesAndFolders());
    }
  } catch (error) {
    dispatch(persistTemporaryItemFailure(item));

    Notify.error({
      title: `Unable to create ${item.isFolder ? 'folder' : 'file'}`,
      description: ErrorHandler.getNotificationError(error),
    });
  }
};
